import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Users } from "lucide-react";

/**
 * Shows a "X people learning now" indicator.
 * Fetches real active session count from the database (last 10 minutes).
 * Clicking navigates to the public analytics page with 24h filter.
 */
export function LiveLearnerCount() {
  const [isVisible, setIsVisible] = React.useState(false);

  // Fetch real active learner count
  const { data: count } = useQuery({
    queryKey: ["active-learner-count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_active_learner_count");
      if (error) {
        console.error("Failed to fetch active learner count:", error);
        return null;
      }
      return data as number;
    },
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // Refresh every minute
    refetchOnWindowFocus: false,
  });

  // Fade in after mount
  React.useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Don't show if count is null/undefined (loading or error)
  if (count === null || count === undefined) {
    return null;
  }

  const isAlone = count === 0;

  return (
    <Link
      to="/analytics?filter=24h"
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
        text-xs font-medium transition-all duration-500 hover:bg-primary/20
        ${isAlone ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}
      `}
    >
      {!isAlone && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
        </span>
      )}
      <Users className="h-3 w-3" />
      <span className={isAlone ? "text-center leading-tight" : ""}>
        {isAlone ? (
          <>You're the only learner<br />here for now</>
        ) : (
          `${count} learning now`
        )}
      </span>
    </Link>
  );
}
